import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import { BORDER_CITIES_BY_STATE, BORDER_STATES } from '../../constants/constants';
import './PushTest.css';

function cityDisplayName(state, city) {
  const rawCity = String(city || '').trim();
  const rawState = String(state || '').trim();
  const inState = (BORDER_CITIES_BY_STATE[rawState] || []).find((item) => item.code === rawCity);
  if (inState) return inState.name;
  const anywhere = Object.values(BORDER_CITIES_BY_STATE)
    .flat()
    .find((item) => item.code === rawCity);
  return anywhere?.name || rawCity;
}

function stateDisplayName(state) {
  const raw = String(state || '').trim();
  return BORDER_STATES.find((item) => item.code === raw)?.name || raw;
}

const REASONS = {
  fcm_disabled: 'Firebase aún no puede enviar. Falta la clave de cuenta de servicio en el worker.',
  user_missing: 'Ese usuario no existe.',
  no_devices: 'Ese usuario no tiene un teléfono registrado. Abre la app con la sesión iniciada.',
  notifications_off: 'Ese usuario apagó las notificaciones.',
  failed: 'Firebase rechazó el envío.',
  partial: 'Llegó a algunos dispositivos y falló en otros.',
  sent: 'Enviada.',
};

export default function PushTest() {
  const toast = useRef(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [event, setEvent] = useState('official');
  const [cityId, setCityId] = useState('');
  const [crossingName, setCrossingName] = useState('');
  const [text, setText] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/push_test/meta');
        setMeta(data);
      } catch {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar la prueba de avisos',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setUsers([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get('/mobile_users', { params: { q, limit: 8 } });
        setUsers(data.items || []);
      } catch {
        setUsers([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const city = useMemo(
    () => (meta?.cities || []).find((item) => item.id === cityId) || null,
    [meta, cityId]
  );

  const crossingOptions = (city?.crossings || []).map((item) => {
    if (typeof item === 'string') return { label: item, value: item };
    return {
      label: item.label || item.value,
      value: item.value || item.label,
    };
  });

  async function send() {
    if (!userId || !event) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Falta algo',
        detail: 'Elige un usuario y un evento',
      });
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/push_test', {
        userId,
        event,
        cityGeoId: cityId || undefined,
        cityName: city ? cityDisplayName(city.state, city.city) : undefined,
        crossingName: crossingName || undefined,
        text: text.trim() || undefined,
      });
      toast.current?.show({
        severity: 'success',
        summary: 'Prueba enviada',
        detail: REASONS[data.reason] || 'Enviada',
      });
    } catch (error) {
      const reason = error?.response?.data?.reason;
      toast.current?.show({
        severity: 'error',
        summary: 'No salió',
        detail: REASONS[reason] || error?.response?.data?.error || 'No se pudo enviar',
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="push-test">
      <Toast ref={toast} />
      <Card title="Test push notifications">
        <p className="push-test-lead">
          Manda una sola notificación de prueba a un usuario. No publica nada y no avisa a la ciudad.
        </p>
        {meta && !meta.fcmReady ? (
          <Message
            severity="warn"
            className="push-test-warn"
            text="El worker todavía no tiene la clave de Firebase. La prueba va a fallar hasta configurarla."
          />
        ) : null}
        {loading ? (
          <i className="pi pi-spin pi-spinner" />
        ) : (
          <div className="push-test-fields">
            <label>
              Usuario
              <InputText
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nombre o correo"
                className="w-full"
              />
            </label>
            {users.length ? (
              <div className="push-test-users">
                {users.map((user) => (
                  <button
                    key={user._id}
                    type="button"
                    className={user._id === userId ? 'is-selected' : ''}
                    onClick={() => setUserId(user._id)}
                  >
                    <strong>{user.displayName || user.communityName || 'Usuario'}</strong>
                    <span>{user.email}</span>
                  </button>
                ))}
              </div>
            ) : null}
            <label>
              Evento
              <Dropdown
                value={event}
                options={(meta?.events || []).map((item) => ({
                  label: item.label,
                  value: item.id,
                }))}
                onChange={(e) => setEvent(e.value)}
                className="w-full"
              />
            </label>
            <label>
              Ciudad
              <Dropdown
                value={cityId}
                options={(meta?.cities || [])
                  .map((item) => ({
                    label: [cityDisplayName(item.state, item.city), stateDisplayName(item.state)]
                      .filter(Boolean)
                      .join(', '),
                    value: item.id,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label, 'es'))}
                onChange={(e) => {
                  setCityId(e.value);
                  setCrossingName('');
                }}
                placeholder="Opcional"
                showClear
                className="w-full"
              />
            </label>
            <label>
              Cruce
              <Dropdown
                value={crossingName}
                options={crossingOptions}
                onChange={(e) => setCrossingName(e.value)}
                placeholder={city ? 'Opcional' : 'Elige una ciudad primero'}
                disabled={!city}
                showClear
                className="w-full"
              />
            </label>
            <label>
              Texto del aviso oficial
              <InputTextarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                autoResize
                className="w-full"
                placeholder="Solo se usa en el evento de aviso oficial"
              />
            </label>
            <Button
              label="Enviar prueba"
              icon="pi pi-send"
              loading={sending}
              onClick={send}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
